import React, { useEffect, useState, FC } from "react";
import "./index.scss";
import Dropdown from "../../static/Dropdown/Dropdown";
import { getMediaDevice, omitNation } from "../../helpers";
import {
  getMostTitledFilters,
  getMostTitledPlayers
} from "../../services/mostTitles";
import { Carousel } from "../Carousel/Carousel";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";
import { dropdownItem } from "packages/static/Dropdown/types";

interface Props {
  circuitCode: string;
  title: string;
}

interface IMostTitledPlayer {
  titles: number;
  playerId: number;
  givenName: string;
  familyName: string;
  playerNationalityCode: string;
  playerNationality: string;
  playerProfileLink: string;
  profileImage: string;
  fullName: string;
}
interface ICode {
  name: string;
  code: string;
}

const itemsToDisplay = { DESKTOPXL: 4, DESKTOP: 4, TABLET: 2, MOBILE: 1 };

const filterLabels = {
  year: "Year",
  matchTypeCode: "Match Type",
  category: "Category",
  surfaceCode: "Surface"
};

const Slides: FC<{
  page: number;
  itemsToDisplay: Record<string, number>;
  players: IMostTitledPlayer[];
}> = ({ page, itemsToDisplay, players }) => {
  return (
    <ReactCSSTransitionGroup
      transitionName="carousel__animation"
      transitionEnterTimeout={500}
      transitionLeaveTimeout={500}
      className={`carousel__animation-container`}
    >
      <div className="most-titles__list">
        {players
          .slice(
            page * itemsToDisplay[getMediaDevice()],
            (page + 1) * itemsToDisplay[getMediaDevice()]
          )
          .map((player: IMostTitledPlayer) => {
            return <Player key={player.playerId} player={player} />;
          })}
      </div>
    </ReactCSSTransitionGroup>
  );
};

const Player: FC<{ player: IMostTitledPlayer }> = ({ player }) => {
  return (
    <div className="most-titles__player">
      <div className="most-titles__player-block">
        <div
          className="most-titles__player-image"
          style={{
            backgroundImage: `url("${player.profileImage}")`
          }}
        />
        <div className="most-titles__player-detail">
          <a className="name" href={player.playerProfileLink}>
            <p className="name-first">{player.givenName}</p>
            <p className="name-last">{player.familyName}</p>
          </a>
          <div className="titles">
            <span>{player.titles}</span>
            titles
          </div>
          <div className="country">
            <div
              className={`itf-flags itf-flags--${player.playerNationalityCode}`}
            ></div>
            <div>{omitNation(player.playerNationalityCode)}</div>
          </div>
        </div>
      </div>
      <a href={player.playerProfileLink} className="most-titles__player-link">
        <span>View profile</span>
      </a>
    </div>
  );
};

const MostTitles: FC<Props> = ({ circuitCode, title }) => {
  let [isLoading, setIsLoading] = useState<boolean>(false);
  let [isListLoading, setIsListLoading] = useState<boolean>(false);
  let [filter, setFilter] = useState({
    year: "",
    matchTypeCode: "",
    category: "",
    surfaceCode: ""
  });
  let [filters, setFilters] = useState<Record<string, any[]>>({
    year: [],
    matchTypeCode: [],
    surfaceCode: [],
    category: []
  });
  let [players, setPlayers] = useState<IMostTitledPlayer[]>([]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const emptyItem = {
          label: "All",
          value: ""
        };
        const {
          years,
          matchTypes,
          surfaces,
          categories
        } = await getMostTitledFilters(circuitCode);
        const year = years.map((year: number) => ({
          label: year.toString(),
          value: year
        }));
        const matchTypeCode = matchTypes.map((code: ICode) => ({
          label: code.name,
          value: code.code
        }));
        const surfaceCode = surfaces.map((surface: ICode) => ({
          label: surface.name,
          value: surface.code
        }));
        const category = categories.map((category: string) => ({
          label: category,
          value: category
        }));
        surfaceCode.unshift(emptyItem);
        category.unshift(emptyItem);
        setFilters({ year, matchTypeCode, surfaceCode, category });
        setFilter(current => {
          return {
            ...current,
            year: years.length ? years[0] : "",
            matchTypeCode: matchTypes.length ? matchTypes[0].code : ""
          };
        });
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!Object.values(filter).some(item => item.length)) return;
      setIsListLoading(true);
      try {
        const players: IMostTitledPlayer[] = await getMostTitledPlayers({
          circuitCode,
          ...filter
        });
        setPlayers(players.slice(0, 4));
      } finally {
        setIsListLoading(false);
      }
    })();
  }, [filter]);

  const getLabelText = (label: string) => {
    const value = filter[label];
    const text = filters[label].find(item => item.value === value);
    return text ? text.label : "All";
  };

  return (
    <>
      <div
        className={`most-titles ${
          isLoading || isListLoading ? "page-loading" : ""
        }`}
      >
        {isLoading ? null : (
          <>
            <div className="most-titles__top">
              <h2>{title}</h2>
              <div className="most-titles__filters">
                {Object.keys(filters).map((label: string) => (
                  <Dropdown
                    key={label}
                    placeholder={`${filterLabels[label]}: ${getLabelText(
                      label
                    )}`}
                    options={filters[label]}
                    value={filter[label]}
                    className="most-titles__filters-field"
                    onChange={({ value }: dropdownItem) => {
                      setFilter(current => {
                        return { ...current, [label]: value };
                      });
                    }}
                  />
                ))}
              </div>
            </div>
            {isListLoading ? null : players && players.length ? (
              <Carousel
                totalItems={players.length}
                itemsToDisplay={itemsToDisplay}
                pagerPerItem
              >
                <Slides
                  page={0}
                  players={players}
                  itemsToDisplay={itemsToDisplay}
                />
              </Carousel>
            ) : (
              <p>No players available</p>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default MostTitles;
